import { ApiObjBaseData } from '../../../api/object/base_data/ApiObjBaseData';
import { UtilArray } from '../../../util/code/UtilArray';
import { CountFilterParams } from './CountFilterParams';

/**
 * Holds count for a single filter
 * along current params the count is based on.
 */
export class CountFilterDataItem {

    /** Waiting for request to be sent. */
    public static readonly STATE_WAITING = 1;

    /** API Request sent, waiting for response. */
    public static readonly STATE_REQUEST_SENT = 2;

    /** A valid count can be shown in UI. End state */
    public static readonly STATE_READY = 3;

    public readonly filter_id: number;
    public readonly nix_option_id: number;
    public readonly source_id: number;
    public readonly filters_selected_from_other_group: Array<number>;
    public readonly map_area_ids: Array<number>;
    public readonly use_number_source: boolean;
    public state : number;
    public count: number;

    // ================================================================
    // === Create
    // ================================================================

    public static createNew(filterId: number, countParams: CountFilterParams, baseData: ApiObjBaseData) : CountFilterDataItem {
        return new CountFilterDataItem(filterId, countParams, baseData, undefined);
    }

    public static createClone(original: CountFilterDataItem) {
        return new CountFilterDataItem(undefined, undefined, undefined, original);
    }

    private constructor(
        filterId: number|undefined,
        countParams: CountFilterParams|undefined,
        baseData: ApiObjBaseData|undefined,
        original: CountFilterDataItem|undefined) {

        if (filterId !== undefined && countParams !== undefined && baseData !== undefined) {
            this.filter_id = filterId;
            this.nix_option_id = countParams.nix_option_id;
            this.source_id = countParams.source_id;

            const thisFilter = baseData.filter_map.get(filterId);
            const tempFilterIds : Array<number> = [];
            if (thisFilter !== undefined) {
                for (const tempId of countParams.filter_ids_selected) {
                    const tempFilter = baseData.filter_map.get(tempId);
                    if (tempFilter !== undefined && tempFilter.getFilterSelectGroup() !== thisFilter.getFilterSelectGroup()) {
                        tempFilterIds.push(tempId);
                    }
                }
            }
            this.filters_selected_from_other_group = UtilArray.createUniqueSortedIntArray(tempFilterIds);

            this.map_area_ids = [];
            for (const item of countParams.map_areas) {
                this.map_area_ids.push(item.id);
            }
            this.use_number_source = countParams.use_number_source;

            this.state = CountFilterDataItem.STATE_WAITING;
            this.count = 0;

        } else if (original !== undefined) {
            this.filter_id = original.filter_id;
            this.nix_option_id = original.nix_option_id;
            this.source_id = original.source_id;
            this.filters_selected_from_other_group = [...original.filters_selected_from_other_group];
            this.map_area_ids = [...original.map_area_ids];
            this.use_number_source = original.use_number_source;
            this.state = original.state;
            this.count = original.count;

        } else {
            throw Error("Unexpected case");
        }
    }

    // ================================================================
    // === State change
    // ================================================================

    /**
     * Checks if params matches params of the item.
     * This is used to determine if the count returned by the api can be used.
     * 
     * @param nixOptionId 
     * @param sourceId 
     * @param selectedFilters 
     * @param mapAreaIds 
     * @returns 
     */
    isValidForParams = (countParams : CountFilterParams, baseData: ApiObjBaseData) : boolean => {

        if (this.nix_option_id != countParams.nix_option_id) {
            return false;
        }

        if (this.source_id != countParams.source_id) {
            return false;
        }

        const thisFilter = baseData.filter_map.get(this.filter_id);
        if (thisFilter === undefined) {
            return false;
        }
        
        let testArray = [];
        for (const otherFilterId of countParams.filter_ids_selected) {
            const otherFilter = baseData.filter_map.get(otherFilterId);
            if (otherFilter === undefined) {
                return false;
            }
            if (otherFilter.getFilterSelectGroup() !== thisFilter.getFilterSelectGroup()) {
                testArray.push(otherFilter.getId());
            }
        }
        testArray = UtilArray.createUniqueSortedIntArray(testArray);
        if (!UtilArray.arrayEquals(testArray, this.filters_selected_from_other_group)) {
            return false;
        }

        const tempMapAreaIds = [];
        for (const item of countParams.map_areas) {
            tempMapAreaIds.push(item.id);
        }
        if (!UtilArray.arrayEquals(this.map_area_ids, tempMapAreaIds)) {
            return false;
        }
        if (this.use_number_source !== countParams.use_number_source) {
            return false;
        }
        return true;
    }

    // /**
    //  * Sets all params the count returned by the api needs to match.
    //  * 
    //  * @param nixOptionId Nix option Id
    //  * @param sourceId 
    //  * @param selectedFilters Selected filters. Filters in same select_group should be excluded.
    //  * @param mapAreaIds Ids of shapes on the map (frontend only, lives during one purchase ).
    //  */
    // setParams = (nixOptionId: number, sourceId: number, selectedFilters: Array<ApiObjFilter>, mapAreaIds: Array<number>, useNumberSource: boolean) : void => {
    //     let newSelectedFilters : Array<number> = [];
    //     for (const filter of selectedFilters) {
    //         if (this.filter_select_group != filter.getFilterSelectGroup()) {
    //             newSelectedFilters.push(filter.getId());
    //         }
    //     }
    //     newSelectedFilters = UtilArray.createUniqueSortedIntArray(newSelectedFilters);
    //     this.filters_selected_from_other_group = newSelectedFilters;
    //     this.nix_option_id = nixOptionId;
    //     this.source_id = sourceId;
    //     this.map_area_ids = mapAreaIds;
    //     this.use_number_source = useNumberSource;
    // }

    // invalidateCount = () : void => {
    //     this.state = CountFilterDataItem.STATE_WAITING;
    //     this.count = 0;
    // }

    setReady = (count: number) : void => {
        this.state = CountFilterDataItem.STATE_READY;
        this.count = count;
    }
}